import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { Transition } from '@headlessui/react';

import Portal from '../HOC/Portal';
import { Close } from '../Icons';

interface MobileSideDrawerProps {
  children?: React.ReactNode;
  className?: string;
  closeable?: boolean;
  header?: () => React.ReactElement;
  footer?: () => React.ReactElement;
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  variant?: 'basic' | 'full';
  withOverlay?: boolean;
}

const MobileSideDrawer = (props: MobileSideDrawerProps) => {
  const {
    children,
    className,
    closeable = true,
    footer,
    isOpen,
    onClose = () => {},
    title,
    variant = 'basic',
    header,
    withOverlay = true,
  } = props;

  const [open, setOpen] = useState<boolean>(isOpen);

  useEffect(() => {
    setOpen(isOpen);
    return () => setOpen(false);
  }, [isOpen]);

  return (
    <Portal>
      <Transition.Root show={open} className="fixed inset-0 z-[999] md:hidden">
        {withOverlay && (
          <Transition.Child
            enter="transition-opacity ease-linear duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-75"
            leave="transition-opacity ease-linear duration-500"
            leaveFrom="opacity-75"
            leaveTo="opacity-0"
            as={React.Fragment}
          >
            <div
              onClick={() => {
                setOpen(false);
                onClose();
              }}
              className="drawer-overlay"
            />
          </Transition.Child>
        )}
        <Transition.Child
          enter="transform transition ease-in-out duration-500"
          enterFrom={
            variant === 'full' ? 'translate-y-full' : 'translate-x-full'
          }
          enterTo={variant === 'full' ? 'translate-y-0' : 'translate-x-0'}
          leave="transform transition ease-in-out duration-500 delay-100"
          leaveFrom={variant === 'full' ? 'translate-y-0' : 'translate-x-0'}
          leaveTo={variant === 'full' ? 'translate-y-full' : 'translate-x-full'}
          as={React.Fragment}
        >
          <div
            className={cx(
              'drawer-body bg-white',
              variant,
              className && className,
              isOpen && 'open'
            )}
          >
            {header ? (
              header()
            ) : (
              <div className="header">
                {closeable && <Close onClick={onClose} className="close-btn" />}
                {title && title}
              </div>
            )}
            <div className="content">{children}</div>
            {footer && <div className="footer">{footer()}</div>}
          </div>
        </Transition.Child>
      </Transition.Root>
    </Portal>
  );
};

export default MobileSideDrawer;
